import type { CSSProperties } from "react";

// Размерът на кутийките в решетка с дръжки на „Маркиране" (десктоп): масите
// в „Списък" (`tables/layout/tablesLayout.ts`) и продуктите в „Плочки"
// (`menu/catalogLayout.ts`). Механизмът е един — дръжките на избраната
// кутийка (`CardResizeGrips`), сивите контури при влачене
// (`CardLayoutPreview`) и хранилището на компютъра (`createLayoutStore`);
// всяка решетка дава само своите граници и класове (`CardResizeSpec`).

export interface CardSize {
  /** Кутийките на ред; null — по подразбиране на решетката. */
  cardColumns: number | null;
  /** Височината на кутийката в px; null — по подразбиране на решетката. */
  cardHeight: number | null;
}

export type GripAxis = "width" | "height" | "both";

/** Каквото знаят дръжките и предварителният изглед за една решетка. */
export interface CardResizeSpec {
  /** Най-тясната кутийка — при по-тясна решетка кутийките на ред намаляват сами. */
  minWidth: number;
  maxColumns: number;
  /** Процепът между кутийките (gap на решетката), px. */
  gap: number;
  heightMin: number;
  heightMax: number;
  /** Докато се влачи — само предварителен изглед (сивите контури). */
  preview: (patch: Partial<CardSize>) => void;
  /** При пускане — предварителният изглед става размерът (и на компютъра). */
  commitPreview: () => void;
  /** Двоен клик на дръжка — по подразбиране, записва се веднага. */
  reset: (patch: Partial<CardSize>) => void;
  /** i18n ключовете на подсказките на дръжките. */
  labelKeys: Record<GripAxis, string>;
  /** Зоната за хващане — по ЦЕЛИЯ десен/долен ръб (без иконка човек не знае
   *  къде да хване — 26.10.2026), по средата на процепа и малко върху ръба
   *  на кутийката, затова зависи от процепа на решетката. */
  gripPlacement: Record<GripAxis, string>;
  /** Махат собствените рамка и фон на копията в предварителния изглед —
   *  контурът е пунктирът отгоре. */
  previewContentClassName: string;
  /** Заоблянето на пунктира — като на кутийката. */
  previewContourClassName: string;
}

/** Колоните и височината, избрани с дръжките (известни чак по време на
 *  работа → inline): N на ред, докато всяка е поне `minWidth` — при
 *  по-тясна решетка колоните намаляват сами (auto-fill). Без избор —
 *  колоните от класовете на решетката. */
export const cardGridStyleOf = (
  { cardColumns, cardHeight }: CardSize,
  minWidth: number,
  gap: number,
): CSSProperties => ({
  gridTemplateColumns:
    cardColumns === null
      ? undefined
      : `repeat(auto-fill, minmax(max(${minWidth}px, calc((100% - ${
          (cardColumns - 1) * gap
        }px) / ${cardColumns} - 0.1px)), 1fr))`,
  ["--card-height" as string]: cardHeight === null ? undefined : `${cardHeight}px`,
});
